import * as api from "./client";
import type {
  AutoApproveToQuotaRequest,
  AutoApproveToQuotaResponse,
} from "./approvalRequests.autoApprove.types";

export type ApprovalRequest = {
  id: string;
  externalId: string;
  contentInput: string | null;
  contentTransformed: string | null;
  contentOutput: string | null;
  langInput: string | null;
  langOutput: string | null;
  contentType: string | null;
  editorialCategoryId: number | null;
  publicationId: number | null;
  publicationName: string | null;
  edition: string | null;
  regionId: number | null;
  regionName: string | null;
  practiceGroupId: number | null;
  practiceGroupName: string | null;
  practiceAreaId: number | null;
  practiceAreaName: string | null;
  tableId: number | null;
  tableName: string | null;
  lawyerName: string | null;
  contactId: number | null;
  firmRef: string | null;
  firmName: string | null;
  transformations: string | null;
  sentiment: string | null;
  status: string;
  updatedAt: string | null;
  createdAt: string | null;
  originalCreatedAt: string | null;
  lockedTo: string | null;
  lockedBy: string | null;
};

export type ApprovalRequestsFilters = {
  limit?: number;
  offset?: number;
  publicationId?: number;
  regionId?: number;
  practiceAreaId?: number;
  tableId?: number;
  edition?: string;
  firmRef?: string;
  langInput?: string;
  langOutput?: string;
  status?: string;
  contentType?: string;
  sentiment?: string;
  onlyUnlocked?: boolean;
  onlyBookers?: boolean;
  lawyerName?: string;
  search?: string;
};

export type ApprovalRequestsResponse = {
  approvalRequests: ApprovalRequest[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId?: string;
};

export type ApprovalRequestResponse = {
  approvalRequest: ApprovalRequest;
  requestId?: string;
};

export type LockResponse = {
  id: string;
  lockedTo: string | null;
  lockedBy: string | null;
  requestId?: string;
};

export type BulkExpectedStatus = "NEW" | "SUSPENDED";

export type BulkDecisionOptions = {
  expectedStatus?: BulkExpectedStatus;
};

export type BulkStatusMismatch = { id: string; status: string };

export type BulkApprovalResponse = {
  updated: ApprovalRequest[];
  lockedIds: string[];
  notFoundIds: string[];
  invalidStatusIds: string[];
  // Optional: approval-api and staff-ui deploy independently, an older approval-api omits them.
  alreadyApprovedIds?: string[];
  alreadyRejectedIds?: string[];
  alreadySuspendedIds?: string[];
  callbackFailedIds?: string[];
  statusMismatches?: BulkStatusMismatch[];
  requestId?: string;
};

function bulkDecisionBody(ids: string[], options?: BulkDecisionOptions) {
  return options?.expectedStatus ? { ids, expectedStatus: options.expectedStatus } : { ids };
}

export type ApprovalStatistics = {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
  suspended: number;
  approvedWithoutChanges: number;
  approvedWithChanges: number;
};

export type ApprovalStatisticsFilters = {
  dateFrom?: string;
  dateTo?: string;
  edition?: string;
  publicationId?: number;
};

export type ApprovalStatisticsResponse = {
  statistics: ApprovalStatistics;
  requestId?: string;
};

export type {
  AutoApproveToQuotaRequest,
  AutoApproveLevelSummary,
  AutoApprovePerFirmRow,
  AutoApprovePerBpaRow,
  AutoApproveApprovedItem,
  AutoApproveExecution,
  AutoApproveToQuotaResponse,
} from "./approvalRequests.autoApprove.types";

export const approvalRequests = {
  getAll: async (filters?: ApprovalRequestsFilters): Promise<ApprovalRequestsResponse> => {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());
    if (filters?.publicationId !== undefined)
      params.append("publicationId", filters.publicationId.toString());
    if (filters?.regionId !== undefined) params.append("regionId", filters.regionId.toString());
    if (filters?.practiceAreaId !== undefined)
      params.append("practiceAreaId", filters.practiceAreaId.toString());
    if (filters?.tableId !== undefined) params.append("tableId", filters.tableId.toString());
    if (filters?.edition) params.append("edition", filters.edition);
    if (filters?.firmRef && filters.firmRef.trim()) params.append("firmRef", filters.firmRef);
    if (filters?.langInput) params.append("langInput", filters.langInput);
    if (filters?.langOutput) params.append("langOutput", filters.langOutput);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.contentType) params.append("contentType", filters.contentType);
    if (filters?.sentiment) params.append("sentiment", filters.sentiment);
    if (filters?.onlyUnlocked !== undefined) {
      params.append("onlyUnlocked", filters.onlyUnlocked.toString());
    }
    if (filters?.onlyBookers !== undefined) {
      params.append("onlyBookers", filters.onlyBookers.toString());
    }
    if (filters?.lawyerName) params.append("lawyerName", filters.lawyerName);
    if (filters?.search) params.append("search", filters.search);

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<ApprovalRequestsResponse>(`/v1/approval/approval-requests${query}`);
  },
  getById: async (id: string): Promise<ApprovalRequestResponse> => {
    return api.get<ApprovalRequestResponse>(`/v1/approval/approval-requests/${id}`);
  },
  lock: async (id: string): Promise<LockResponse> => {
    return api.post<LockResponse>(`/v1/approval/approval-requests/${id}/lock`, {});
  },
  unlock: async (id: string): Promise<LockResponse> => {
    return api.post<LockResponse>(`/v1/approval/approval-requests/${id}/unlock`, {});
  },
  approve: async (id: string): Promise<ApprovalRequestResponse> => {
    return api.post<ApprovalRequestResponse>(`/v1/approval/approval-requests/${id}/approve`, {});
  },
  reject: async (id: string): Promise<ApprovalRequestResponse> => {
    return api.post<ApprovalRequestResponse>(`/v1/approval/approval-requests/${id}/reject`, {});
  },
  suspend: async (id: string): Promise<ApprovalRequestResponse> => {
    return api.post<ApprovalRequestResponse>(`/v1/approval/approval-requests/${id}/suspend`, {});
  },
  reviewAgain: async (id: string): Promise<ApprovalRequestResponse> => {
    return api.post<ApprovalRequestResponse>(
      `/v1/approval/approval-requests/${id}/review-again`,
      {},
    );
  },
  updateContentOutput: async (
    id: string,
    contentOutput: string,
  ): Promise<ApprovalRequestResponse> => {
    return api.patch<ApprovalRequestResponse>(
      `/v1/approval/approval-requests/${id}/content-output`,
      { contentOutput },
    );
  },
  updateContactId: async (id: string, contactId: number): Promise<ApprovalRequestResponse> => {
    return api.patch<ApprovalRequestResponse>(`/v1/approval/approval-requests/${id}/contact-id`, {
      contactId,
    });
  },
  bulkApprove: async (
    ids: string[],
    options?: BulkDecisionOptions,
  ): Promise<BulkApprovalResponse> => {
    return api.post<BulkApprovalResponse>(
      `/v1/approval/approval-requests/bulk/approve`,
      bulkDecisionBody(ids, options),
    );
  },
  bulkReject: async (
    ids: string[],
    options?: BulkDecisionOptions,
  ): Promise<BulkApprovalResponse> => {
    return api.post<BulkApprovalResponse>(
      `/v1/approval/approval-requests/bulk/reject`,
      bulkDecisionBody(ids, options),
    );
  },
  bulkSuspend: async (
    ids: string[],
    options?: BulkDecisionOptions,
  ): Promise<BulkApprovalResponse> => {
    return api.post<BulkApprovalResponse>(
      `/v1/approval/approval-requests/bulk/suspend`,
      bulkDecisionBody(ids, options),
    );
  },
  autoApproveToQuota: async (
    body: AutoApproveToQuotaRequest,
  ): Promise<AutoApproveToQuotaResponse> => {
    return api.post<AutoApproveToQuotaResponse>(
      `/v1/approval/approval-requests/auto-approve-to-quota`,
      body,
    );
  },
  getStatistics: async (
    filters?: ApprovalStatisticsFilters,
  ): Promise<ApprovalStatisticsResponse> => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.append("dateTo", filters.dateTo);
    if (filters?.edition) params.append("edition", filters.edition);
    if (filters?.publicationId !== undefined)
      params.append("publicationId", filters.publicationId.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<ApprovalStatisticsResponse>(`/v1/approval/approval-requests/statistics${query}`);
  },
};
